import { STEP } from "#src/constants/step.ts";
import {
  defineStore,
  generateInitialState,
  resetState,
  type ToSetupState,
} from "#src/stores/state-wrapper.ts";
import {
  type DataRequirement,
  type DataRequirements,
  REQS,
} from "#src/constants/data-requirements.ts";
import { ROLE_TYPE } from "#src/types/Role.ts";

import { useStepPhysicianStore } from "#src/stores/step-physician.ts";
import {
  getPartyStore,
  useEappStore,
} from "#src/stores/electronic-application.ts";
import { useStepPaymentStore } from "#src/stores/step-payment.ts";
import { computed, type Ref, ref } from "vue";
import { type Pinia, type StoreGeneric } from "pinia";
import {
  type BaseStepStoreState,
  type BaseStepStore,
} from "#src/types/StepStore.ts";

function initializeRequiredFields(reqs?: DataRequirements) {
  const required = (requirement: DataRequirement): boolean =>
    reqs?.[requirement] || false;
  return {
    address: required(REQS.SECONDARY_ADDRESSEE_ADDRESS),
    phone: required(REQS.SECONDARY_ADDRESSEE_PHONE),
    email: required(REQS.SECONDARY_ADDRESSEE_EMAIL),
    relationship: required(REQS.SECONDARY_ADDRESSEE_RELATIONSHIP),
    birthdate: required(REQS.SECONDARY_ADDRESSEE_BIRTHDATE),
  };
}

interface SecondaryAddresseeState extends BaseStepStoreState {
  requiredFields: Record<string, boolean>;
}

interface SecondaryAddresseeStep
  extends BaseStepStore,
    ToSetupState<SecondaryAddresseeState> {
  secondaryAddresseeId: Ref<string | null>;
  setStepRequirements(srq: DataRequirements): void;
}

function initialState(): SecondaryAddresseeState {
  return {
    inactive: false,
    requiredFields: initializeRequiredFields(),
  };
}

function initializeSecondaryAddresseeStep(
  pinia: Pinia
): SecondaryAddresseeStep {
  const state = generateInitialState<SecondaryAddresseeState>(initialState());

  function $reset() {
    resetState<SecondaryAddresseeState>(state, initialState());
  }

  const step = computed(() => STEP.SECONDARY_ADDRESSEE);
  const previous = computed(() => {
    const physician = useStepPhysicianStore(pinia);
    if (physician.inactive) return physician.previous;
    return physician.step;
  });
  const next = computed(() => {
    const payment = useStepPaymentStore(pinia);
    if (payment.inactive) return payment.next;
    return payment.step;
  });
  const secondaryAddresseeId = computed(() => {
    const eApp = useEappStore(pinia);
    return (
      eApp.parties.find((p: string) => {
        const store = getPartyStore(p, pinia);
        return Boolean(store.roles[ROLE_TYPE.SECONDARY_ADDRESSEE]);
      }) || null
    );
  });
  function setStepRequirements(srq: DataRequirements) {
    state.inactive.value = !srq[REQS.SECONDARY_ADDRESSEE];
    state.requiredFields.value = initializeRequiredFields(srq);
  }

  return {
    ...state,
    step,
    previous,
    next,
    setStepRequirements,
    $reset,

    secondaryAddresseeId,
  };
}

export const useStepSecondaryAddresseeStore = (
  pinia: Pinia,
  hot?: StoreGeneric
) => {
  return defineStore<string, SecondaryAddresseeStep>(
    "step-secondary-addressee",
    () => {
      return initializeSecondaryAddresseeStep(pinia);
    }
  )(pinia, hot);
};
